import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { COL_HEADER_SX, FILTER_SELECT_SX } from "@/constants/users";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useUpdateUserRole } from "@/hooks/useUsers";
import type { User } from "@/interfaces/user";
import { ALL_ROLES, ROLES, type Role } from "@/lib/roles";
import { resolveRoleLabels } from "@/utils/role-label-helper";

interface EditRoleDialogProps {
  user: User | null;
  onClose: () => void;
  canEdit: boolean;
}

export default function EditRoleDialog({ user, onClose, canEdit }: EditRoleDialogProps) {
  const [selected, setSelected] = useState<Role>(
    user?.orgRole ?? user?.gfgRole ?? ROLES.CLIENT_ADMIN,
  );
  const [error, setError] = useState<string | null>(null);
  const { mutate, isPending } = useUpdateUserRole();

  const { can } = usePermissions();
  const { labelFor, roles } = useRoles();
  const canCreateAdmin = can("create", "ADMIN");
  const availableRoles = ALL_ROLES.filter(
    (r) => canCreateAdmin || (r !== ROLES.GFG_SUPER_ADMIN && r !== ROLES.GFG_STANDARD_ADMIN),
  );

  // Two roles can share a label (e.g. VIEW_ONLY and ORG_VIEW_ONLY both read "View Only").
  // Qualify only the labels that actually collide within this dropdown so an unambiguous
  // rename never needs a code change here.
  const displayLabel = resolveRoleLabels(
    availableRoles.map((r) => ({
      key: r,
      label: labelFor(r),
      level: roles.find((option) => option.key === r)?.level,
    })),
  );

  useEffect(() => {
    if (user) {
      setSelected(user.orgRole ?? user.gfgRole ?? ROLES.CLIENT_ADMIN);
      setError(null);
    }
  }, [user]);

  const handleSave = () => {
    if (!user) return;
    setError(null);
    mutate(
      { userId: user.id, role: selected },
      {
        onSuccess: onClose,
        onError: (err) => setError(err instanceof Error ? err.message : "Failed to update role."),
      },
    );
  };

  return (
    <Dialog
      open={!!user}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "var(--radius-card)", boxShadow: "0px 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 2.5,
          pb: 2,
          px: 3,
        }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "var(--color-rodeo-black)" }}
        >
          Update Role
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            border: "1px solid var(--color-smoke)",
            borderRadius: "var(--radius-btn)",
            bgcolor: "background.paper",
            color: "var(--color-graphite)",
            width: 44,
            height: 44,
            "&:hover": { bgcolor: "var(--color-wax)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {user && (
          <Stack gap={2.5}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--color-rodeo-black)" }}>
                {user.name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                {user.email}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ ...COL_HEADER_SX, display: "block", mb: 1 }}>New Role</Typography>
              <Select
                value={selected}
                onChange={(e) => setSelected(e.target.value as Role)}
                fullWidth
                sx={FILTER_SELECT_SX}
              >
                {availableRoles.map((r) => (
                  <MenuItem
                    key={r}
                    value={r}
                    sx={{
                      fontFamily: "var(--font-lato)",
                      fontSize: 14,
                      color: "var(--color-rodeo-black)",
                    }}
                  >
                    {displayLabel(r)}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {error && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "var(--color-good-wine)",
                  bgcolor: "var(--color-good-wine-bg)",
                  px: 1.5,
                  py: 1,
                  borderRadius: "var(--radius-btn)",
                }}
              >
                {error}
              </Typography>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" pt={0.5}>
              <Button
                onClick={onClose}
                disableRipple
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "1.98px",
                  color: "var(--color-dark-dust)",
                  textTransform: "uppercase",
                  p: 0,
                  minWidth: 0,
                  "&:hover": { bgcolor: "transparent", color: "var(--color-rodeo-black)" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isPending || selected === (user.orgRole ?? user.gfgRole) || !canEdit}
                disableElevation
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "1.98px",
                  borderRadius: "var(--radius-btn)",
                  textTransform: "uppercase",
                  height: 44,
                  bgcolor: "var(--color-good-wine)",
                  color: "white",
                  "&:hover": { bgcolor: "var(--color-good-wine-2)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--color-smoke)",
                    color: "var(--color-graphite)",
                  },
                }}
              >
                {isPending ? "Saving…" : canEdit ? "Save Role" : "Read Only"}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
